import { Box, Card } from '@mui/material';
import { MUIDataTableColumnDef } from 'mui-datatables';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CloudFunctionApi } from '../../../../cloudfunctions';
import { UserDetails } from '../../../../constants/Common';
import type { User } from '../../../../firebase/firebase';
import { FirebaseApi } from '../../../../firebase/firebaseApi';
import { useAbortController } from '../../../../hooks/useAbortController';
import {
	EmployeeLink,
	ProjectTrackingLinks,
} from '../../../../models/Integrations/ProjectTrackingIntegration';
import { linkTableSort } from '../../../helpers/muiDataTableCustomSorts';
import { employeeIntegrationTableRowSearch } from '../../../Integrations/Components/Helpers/employeeIntegrationTableRowSearch';
import { IntegrationLinkButton } from '../../../Integrations/Components/IntegrationLinkButton/IntegrationLinkButton';
import {
	IntegrationLinkStatus,
	IntegrationStatusChip,
} from '../../../Integrations/Components/IntegrationStatusChip/IntegrationStatusChip';
import { IntegrationTable } from '../../../Integrations/Components/IntegrationTable/IntegrationTable';
import { IntegrationTextLinkCell } from '../../../Integrations/Components/IntegrationTextLinkCell';
import {
	EmployeeIntegrationTableRow,
	TempEmployeeLink,
	isEmployeeLink,
} from '../../Common/Employees/Models/employeeTab';
import { getLinkStatus } from '../CATProjectsConstants';

export type EmployeesTabProps = {
	firebaseApi: Pick<
		FirebaseApi,
		| 'subscribeNonKioskUsersByCompany'
		| 'projectTrackingIntegrationEmployeeLinksSubscription'
	>;
	cloudFunctionApi: Pick<
		CloudFunctionApi,
		| 'createProjectTrackingEmployeeLink'
		| 'deleteProjectTrackingEmployeeLink'
	>;
	userDetails: UserDetails;
	user: User;
};

export const EmployeesTab = ({
	firebaseApi,
	cloudFunctionApi,
	user,
	userDetails,
}: EmployeesTabProps): JSX.Element => {
	const abortSignal = useAbortController();
	const [searchParams] = useSearchParams();

	const [selected, setSelected] = useState<number | null>(null);
	const [employees, setEmployees] = useState<Record<string, UserDetails>>({});
	const [employeeLinks, setEmployeeLinks] = useState<
		Record<string, EmployeeLink>
	>({});
	const [newLinks, setNewLinks] = useState<Record<string, EmployeeLink>>({});
	const [tableData, setTableData] = useState<EmployeeIntegrationTableRow[]>(
		[],
	);
	const [loading, setLoading] = useState({
		employees: true,
		employeeLinks: true,
	});
	const [linkingRecords, setLinkingRecords] = useState<
		Record<string, boolean>
	>({});

	const numCells = 4;
	const cellWidth = { width: `${100 / numCells}%` };
	const cellWidthExtra = `${(100 / numCells) * 1.25}%`;
	const cellWidthLess = `${(100 / numCells) * 0.75}%`;

	useEffect(() => {
		if (userDetails.companyID === '') {
			return;
		}
		setLoading((prev) => ({ ...prev, employees: true }));
		return firebaseApi.subscribeNonKioskUsersByCompany(
			userDetails.companyID,
			(users) => {
				const employeeRecord = users.reduce<
					Record<string, UserDetails>
				>(
					(prev, current) => ({ ...prev, [current.userID]: current }),
					{},
				);

				setEmployees(employeeRecord);
				setLoading((prev) => ({ ...prev, employees: false }));
			},
		);
	}, [firebaseApi, userDetails]);

	useEffect(() => {
		if (userDetails.companyID === '') {
			return;
		}
		setLoading((prev) => ({ ...prev, employeeLinks: true }));
		return firebaseApi.projectTrackingIntegrationEmployeeLinksSubscription(
			userDetails.companyID,
			(links) => {
				setEmployeeLinks(links);
				setLoading((prev) => ({ ...prev, employeeLinks: false }));
			},
		);
	}, [firebaseApi, userDetails]);

	useEffect(() => {
		const tableData = Object.values(employees).map((employee) =>
			mapTableData(employee, employeeLinks, newLinks),
		);

		setTableData(tableData);
	}, [employeeLinks, employees, newLinks]);

	useEffect(() => {
		const name = searchParams.get('name');
		const index = tableData.findIndex((row) => row.name === name);
		setSelected(index === -1 ? null : index);
	}, [tableData, searchParams]);

	const setCellHeaderProps = (): {
		style: {
			width: string;
		};
	} => ({
		style: { ...cellWidth },
	});

	const mapTableData = (
		employee: UserDetails,
		links: Record<string, EmployeeLink>,
		newLinks: Record<string, EmployeeLink>,
	): EmployeeIntegrationTableRow => {
		const link: EmployeeLink | null = links[employee.userID] ?? null;
		const status = getLinkStatus(link);

		return {
			id: employee.userID,
			name: employee.displayName,
			status: status,
			account: link ??
				newLinks[employee.userID] ?? { id: employee.userID },
		};
	};

	const createLink = async (link: EmployeeLink): Promise<void> => {
		await cloudFunctionApi.createProjectTrackingEmployeeLink(
			abortSignal,
			user,
			link,
		);
	};

	const deleteLink = async (link: EmployeeLink): Promise<void> => {
		await cloudFunctionApi.deleteProjectTrackingEmployeeLink(
			abortSignal,
			user,
			link,
		);
	};

	const onLinkCellChange =
		(employeeLink: TempEmployeeLink, employee: UserDetails) =>
		(integrationID?: string): void => {
			if (integrationID === undefined) {
				setNewLinks((prev) => {
					const { [employeeLink.id]: _, ...remaining } = prev;
					return remaining;
				});
				return;
			}

			setNewLinks((prev) => ({
				...prev,
				[employeeLink.id]: {
					id: employeeLink.id,
					name: employee.displayName,
					integrationID: integrationID,
					integrationName: integrationID, // could also choose to leave this blank?
					type: ProjectTrackingLinks.Employee,
				},
			}));
		};

	const renderOptionButton = (employeeID: string): JSX.Element => {
		const link: EmployeeLink | undefined = employeeLinks[employeeID];
		const newLink: EmployeeLink | undefined = newLinks[employeeID];

		const handleCreateLink = async (): Promise<void> => {
			setLinkingRecords((prev) => ({ ...prev, [employeeID]: true }));
			await createLink(newLink);
			setNewLinks((prev) => {
				const { [employeeID]: _, ...remaining } = prev;
				return remaining;
			});
			setLinkingRecords((prev) => {
				const { [employeeID]: _, ...remaining } = prev;
				return remaining;
			});
		};
		const handleDeleteLink = async (): Promise<void> => {
			setLinkingRecords((prev) => ({ ...prev, [employeeID]: true }));
			await deleteLink(link);
			setLinkingRecords((prev) => {
				const { [employeeID]: _, ...remaining } = prev;
				return remaining;
			});
		};

		const disabled = !link && newLink === undefined;

		return (
			<IntegrationLinkButton
				link={link}
				disabled={disabled}
				handleLinkClick={handleCreateLink}
				handleUnlinkClick={handleDeleteLink}
			/>
		);
	};

	const renderAccount = (employeeLink: TempEmployeeLink): JSX.Element => {
		const trueLink = employeeLinks[employeeLink.id];
		const employee = employees[employeeLink.id];

		const onChange = onLinkCellChange(employeeLink, employee);
		const linking = !!linkingRecords[employeeLink.id];

		return (
			<IntegrationTextLinkCell
				link={trueLink}
				linkIntegrationID={
					isEmployeeLink(employeeLink)
						? employeeLink.integrationID
						: undefined
				}
				onChange={onChange}
				disabled={linking}
			/>
		);
	};

	const columns: MUIDataTableColumnDef[] = [
		{
			name: 'name',
			label: 'Name',
			options: {
				sort: true,
				setCellHeaderProps: () => ({
					style: {
						...setCellHeaderProps().style,
						width: cellWidthExtra,
					},
				}),
			},
		},
		{
			name: 'status',
			label: 'Status',
			options: {
				sort: true,
				setCellHeaderProps: () => ({
					style: {
						...setCellHeaderProps().style,
						width: cellWidthLess,
					},
				}),
				customBodyRender: (value: IntegrationLinkStatus) => (
					<Box display="flex" justifyContent="center">
						<IntegrationStatusChip status={value} />
					</Box>
				),
			},
		},
		{
			name: 'account',
			label: `CATProjects Account`,
			options: {
				sort: true,
				setCellHeaderProps: () => ({
					style: {
						...setCellHeaderProps().style,
						width: cellWidthExtra,
					},
				}),
				sortCompare: linkTableSort,
				customBodyRender: (employeeLink: EmployeeLink) => (
					<Box display="flex" justifyContent="center">
						{renderAccount(employeeLink)}
					</Box>
				),
			},
		},
		{
			name: 'id',
			label: 'Options',
			options: {
				sort: false,
				setCellHeaderProps: () => ({
					style: {
						...setCellHeaderProps().style,
						width: cellWidthLess,
					},
				}),
				customBodyRender: renderOptionButton,
			},
		},
	];

	return (
		<Box flex="1">
			<Card>
				<IntegrationTable
					title="Link Employees"
					data={tableData}
					columns={columns}
					loading={loading.employeeLinks || loading.employees}
					missingDataLabel="Sorry, no employees found"
					centeredColumns={[2, 3, 4]}
					customSearch={employeeIntegrationTableRowSearch}
					selection={[selected, setSelected]}
				/>
			</Card>
		</Box>
	);
};
